import React, { useEffect } from 'react'
import { Dispatch } from 'redux'
import { Form, FormikProps } from 'formik'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { EditionType } from '@graphics/gfx-common/dist/constants'
import { IServerConfig } from '../../types/config'
import { ILanguages } from '../../types/languages'
import { IPackInput, PackType } from '../../types/pack'
import { push } from '../../actions/router'
import {
  publishPack as publishPackAction,
  checkPackBeingPublished,
} from '../../actions/pack'
import { getLanguages } from '../../actions/languages'
import { deleteUploadedPreviews } from '../../actions/file'
import { getDataStreamAllowedDomainsAction } from '../../actions/config'
import { matchId } from '../../services/common'
import {
  transformPackInputToState,
  arePackPublishOptionsChanged,
  arePackPromotionDestinationsChanged,
  areSomeEditionsPublished,
  areSomeEditionsChanged,
} from '../../services/packForm'
import { DetailsTemplate, StyledFormActionWrapper, Space } from '../../layouts'
import { Button, H2, View, NavigationGuard } from '../../components'
import { PackFormFragment } from '../PackForm/PackFormFragment'
import { EditionFormFragment } from '../PackForm/EditionFormFragment'
import { PackPublishOptionsFragment } from '../PackForm/PackPublishOptionsFragment'
import { EditionPublishOptionsFragment } from '../PackForm/EditionPublishOptionsFragment'
import { EditionRevisionFragment } from '../PackForm/EditionRevisionFragment'
import { DatastreamGraphicURLFormFragment } from '../PackForm/DatastreamGraphicURLFormFragment'
import { usePublishedEditionChanged, usePublishedPackChanged } from './hooks'

export interface IUpdatePackForm {
  pack: PackType
  editionId?: string
  serverConfig: IServerConfig
  languages: ILanguages
  formikProps: FormikProps<IPackInput>
  isDeletedFilePresentInEditions: boolean
  isLightGroup?: boolean
  dispatch: Dispatch
}

export const UpdatePackForm = ({
  pack,
  editionId,
  serverConfig,
  languages,
  dispatch,
  isDeletedFilePresentInEditions,
  isLightGroup,
  formikProps,
}: IUpdatePackForm) => {
  const isPublishedEditionChanged = usePublishedEditionChanged(formikProps)
  const isPackChanged = usePublishedPackChanged(formikProps)
  const isPublishing: boolean = get(pack, 'publishing.publishing', false)
  const { editions = [] } = formikProps.values
  const index = editions.findIndex(matchId(editionId))

  useEffect(() => {
    dispatch(getLanguages())
    dispatch(getDataStreamAllowedDomainsAction())
  }, [dispatch])

  useEffect(() => {
    if (isPublishing) {
      dispatch(checkPackBeingPublished())
    }
  }, [dispatch, isPublishing])

  const publishPack = () => {
    dispatch(publishPackAction(transformPackInputToState(formikProps.values)))
  }

  const noPublishLocations = !editions.some((edition) =>
    edition.publishing.locations.some(({ publish }) => publish)
  )
  const packPublishOptionsHaveBeenChanged = arePackPublishOptionsChanged(
    editions
  )
  const packPromotionDestinationsHaveBeenChanged = arePackPromotionDestinationsChanged(
    editions
  )

  const isPublishButtonDisabled =
    isDeletedFilePresentInEditions ||
    !isEmpty(formikProps.errors) ||
    pack.isLoading ||
    isPublishing ||
    (areSomeEditionsPublished(editions)
      ? !(
          areSomeEditionsChanged(editions) ||
          isPackChanged ||
          isPublishedEditionChanged ||
          packPublishOptionsHaveBeenChanged ||
          packPromotionDestinationsHaveBeenChanged
        )
      : !(
          packPublishOptionsHaveBeenChanged ||
          packPromotionDestinationsHaveBeenChanged
        ))

  return pack.id && !languages.isLoading ? (
    <Form id="update-pack-form">
      <DetailsTemplate
        aside={
          <>
            <H2 mb={4}>Publish options</H2>
            <Space mb={6}>
              <Button
                id="publish-button"
                variant="contained"
                color="primary"
                disabled={isPublishButtonDisabled}
                onClick={publishPack}
                fullWidth
              >
                Publish now
              </Button>
            </Space>
            {pack.editions.map(
              (edition, i) =>
                edition.publishing.published && (
                  <View
                    keep
                    notFullHeight
                    edition={edition.id}
                    key={edition.id}
                  >
                    <EditionRevisionFragment
                      index={i}
                      formikProps={formikProps}
                      isFormDisabled={isPublishing}
                    />
                  </View>
                )
            )}
            {isLightGroup && get(pack, 'editions[0].publishing.published') && (
              <EditionRevisionFragment
                index={0}
                formikProps={formikProps}
                isFormDisabled={isPublishing}
              />
            )}
            {editionId === 'overall-pack' && !isLightGroup ? (
              <View tab="publish">
                <PackPublishOptionsFragment
                  disableAddPublishSchedule={
                    isDeletedFilePresentInEditions || noPublishLocations
                  }
                />
              </View>
            ) : (
              <EditionPublishOptionsFragment
                formikProps={formikProps}
                index={index}
                edition={pack.editions[index]}
                isFormDisabled={isPublishing}
              />
            )}
          </>
        }
        contentProps={{ id: 'pack-publish-tab-main' }}
        asideProps={{ id: 'pack-publish-tab-aside' }}
      >
        <H2 mb={4}>Enter metadata and save</H2>
        <div>
          <View keep edition="overall-pack">
            <PackFormFragment
              formikProps={formikProps}
              languages={languages}
              isFormDisabled={isPublishing}
              isLightGroup={isLightGroup}
            />
            {isLightGroup &&
              pack.editions.length !== 0 &&
              pack.editions[0].editionType ===
                EditionType.DataStreamGraphic && (
                <DatastreamGraphicURLFormFragment
                  index={0}
                  disabled={isPublishing}
                  allowedDomains={serverConfig.allowedDomains}
                />
              )}
          </View>
          {pack.editions.map((edition, i) => (
            <View keep edition={edition.id} key={edition.id}>
              <EditionFormFragment
                index={i}
                languages={languages}
                formikProps={formikProps}
                serverConfig={serverConfig}
                isFormDisabled={isPublishing}
              />
            </View>
          ))}
          <StyledFormActionWrapper>
            <Button
              id="cancel-button"
              onClick={() => {
                dispatch(deleteUploadedPreviews(formikProps.values))
                formikProps.resetForm()
                dispatch(
                  push(
                    `/group/${pack.group}/pack/${pack.id}?edition=${editionId}&tab=details`
                  )
                )
              }}
            >
              Cancel
            </Button>
            <Button
              id="submit-form-data"
              type="submit"
              variant="contained"
              color="primary"
              disabled={
                !formikProps.isValid ||
                pack.isLoading ||
                isPublishing ||
                (formikProps.status && formikProps.status.submitDisabled)
              }
            >
              Save
            </Button>
          </StyledFormActionWrapper>
        </div>
      </DetailsTemplate>
      <NavigationGuard isBlocking={formikProps.dirty && !pack.isLoading} />
    </Form>
  ) : null
}
